import React, { useState, useEffect } from 'react';
import { 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp, 
  RefreshCw, 
  Trash2, 
  Eye,
  UserPlus,
  Shield,
  Calendar,
  AlertCircle,
  Mail
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { adminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [subscriberStats, setSubscriberStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [promoteEmail, setPromoteEmail] = useState('');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, analysesData, subscriberData] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAnalyses(0, 50),
        adminAPI.getSubscriberStats().catch((err) => {
          console.error('Subscriber stats fetch failed:', err);
          return null;
        })
      ]);
      setStats(statsData);
      setAnalyses(analysesData);
      setSubscriberStats(subscriberData);
      setError('');
    } catch (err) {
      setError('Failed to load admin data');
      console.error('Admin data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRerunAnalysis = async (analysisId) => {
    setActionLoading(`rerun-${analysisId}`);
    try {
      await adminAPI.rerunAnalysis(analysisId, 'Admin-triggered rerun from dashboard');
      await fetchData(); // Refresh data
      setError('');
    } catch (err) {
      setError(`Failed to rerun analysis: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleDeleteAnalysis = async (analysisId) => {
    if (!window.confirm('Are you sure you want to permanently delete this analysis? This action cannot be undone.')) {
      return;
    }

    setActionLoading(`delete-${analysisId}`);
    try {
      await adminAPI.deleteAnalysis(analysisId);
      await fetchData(); // Refresh data
      setError('');
    } catch (err) {
      setError(`Failed to delete analysis: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading('');
    }
  };

  const handlePromoteUser = async (e) => {
    e.preventDefault();
    if (!promoteEmail.trim()) return;

    setActionLoading('promote');
    try {
      await adminAPI.promoteUser(promoteEmail);
      setPromoteEmail('');
      setError('');
      alert(`Successfully promoted ${promoteEmail} to admin`);
    } catch (err) {
      setError(`Failed to promote user: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading('');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'partial': return 'text-orange-600 bg-orange-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md mx-auto">
          <CardContent className="text-center py-8">
            <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access the admin dashboard.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage analyses, users, and system overview</p>
            </div>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Users</p>
                    <p className="text-2xl font-bold text-gray-900" data-testid="admin-stat-total-users">{stats.total_users}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="admin-stat-subscribers-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Checklist Subscribers</p>
                    <p className="text-2xl font-bold text-gray-900" data-testid="admin-stat-total-subscribers">
                      {subscriberStats?.total_subscribers ?? '—'}
                    </p>
                    {subscriberStats && (
                      <p className="text-xs text-gray-500 mt-1">
                        {subscriberStats.active_subscribers ?? 0} active &middot; +{subscriberStats.new_today ?? 0} today
                      </p>
                    )}
                  </div>
                  <Mail className="h-8 w-8 text-indigo-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Analyses</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_analyses}</p>
                  </div>
                  <FileText className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Recent (7 days)</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.recent_analyses}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">${stats.total_revenue?.toFixed(2) || '0.00'}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Recent Analyses</CardTitle>
                <CardDescription>
                  Manage user analyses, rerun failed processes, and perform cleanup
                </CardDescription>
              </CardHeader>
              <CardContent>
                {analyses.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600">No analyses found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">User</th>
                          <th className="text-left py-2">Package</th>
                          <th className="text-left py-2">Status</th>
                          <th className="text-left py-2">Payment</th>
                          <th className="text-left py-2">Created</th>
                          <th className="text-left py-2">Reruns</th>
                          <th className="text-left py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyses.map((analysis) => (
                          <tr key={analysis.id} className="border-b hover:bg-gray-50">
                            <td className="py-3">
                              <div className="font-medium text-gray-900">
                                {analysis.user_email}
                              </div>
                              <div className="text-xs text-gray-500">
                                ID: {analysis.id.slice(-8)}
                              </div>
                            </td>
                            <td className="py-3">
                              <span className="text-sm capitalize">
                                {analysis.package_type?.replace('_', ' ') || 'N/A'}
                              </span>
                            </td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(analysis.status)}`}>
                                {analysis.status}
                              </span>
                              {analysis.processing_errors?.length > 0 && (
                                <div className="text-xs text-red-600 mt-1">
                                  {analysis.processing_errors.length} error(s)
                                </div>
                              )}
                            </td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(analysis.payment_status)}`}>
                                {analysis.payment_status}
                              </span>
                            </td>
                            <td className="py-3 text-sm text-gray-600">
                              {formatDate(analysis.created_at)}
                            </td>
                            <td className="py-3 text-sm text-gray-600">
                              {analysis.rerun_count || 0}
                            </td>
                            <td className="py-3">
                              <div className="flex items-center space-x-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRerunAnalysis(analysis.id)}
                                  disabled={actionLoading === `rerun-${analysis.id}`}
                                  className="text-xs px-2 py-1 h-7"
                                >
                                  {actionLoading === `rerun-${analysis.id}` ? (
                                    <RefreshCw className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-3 w-3" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteAnalysis(analysis.id)}
                                  disabled={actionLoading === `delete-${analysis.id}`}
                                  className="text-xs px-2 py-1 h-7 text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* User Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <UserPlus className="h-5 w-5" />
                  <span>User Management</span>
                </CardTitle>
                <CardDescription>
                  Promote users to admin role
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePromoteUser} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={promoteEmail}
                    onChange={(e) => setPromoteEmail(e.target.value)}
                    required
                  />
                  <Button 
                    type="submit" 
                    size="sm" 
                    className="w-full"
                    disabled={actionLoading === 'promote'}
                  >
                    {actionLoading === 'promote' ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <UserPlus className="h-4 w-4 mr-2" />
                    )}
                    Promote to Admin
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* System Stats */}
            {stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5" />
                    <span>Status Distribution</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {Object.entries(stats.status_distribution || {}).map(([status, count]) => (
                      <div key={status} className="flex justify-between items-center">
                        <span className="capitalize">{status}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Package Distribution */}
            {stats && (
              <Card>
                <CardHeader>
                  <CardTitle>Package Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {Object.entries(stats.package_distribution || {}).map(([pkg, count]) => (
                      <div key={pkg} className="flex justify-between items-center">
                        <span className="capitalize">{pkg?.replace('_', ' ') || 'N/A'}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;